"use client";

import { useEffect, useRef } from "react";
import { usePagination } from "react-instantsearch";
import { RESULT_PAGE_URL_PARAM } from "@/components/Search/constants";

export function pageIndexFromSearch(search: string): number {
  const raw = Number(new URLSearchParams(search).get(RESULT_PAGE_URL_PARAM));
  return raw > 0 ? raw - 1 : 0;
}

export const SEARCH_PAGE_CHANGE_EVENT = "search-page-change";

export function dispatchSearchUrlChange() {
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function dispatchSearchPageChange() {
  window.dispatchEvent(new Event(SEARCH_PAGE_CHANGE_EVENT));
}

/**
 * When a facet change resets the InstantSearch page, write that page into the
 * current URL. Other query params are left as they are. Clicks and
 * back/forward update the URL themselves; this effect does not call `refine`.
 */
export default function useSyncSearchPage() {
  const { currentRefinement } = usePagination();
  const previousRefinement = useRef<number | null>(null);

  useEffect(() => {
    const previous = previousRefinement.current;
    previousRefinement.current = currentRefinement;

    if (previous === null || previous === currentRefinement) return;
    if (!window.location.pathname.includes("/search")) return;

    const urlPage = pageIndexFromSearch(window.location.search);
    if (urlPage === currentRefinement) return;

    const params = new URLSearchParams(window.location.search);
    params.set(RESULT_PAGE_URL_PARAM, String(currentRefinement + 1));
    const nextSearch = `?${params.toString()}`;
    if (window.location.search === nextSearch) return;

    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${nextSearch}`,
    );
    dispatchSearchUrlChange();
  }, [currentRefinement]);
}
